import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import type {
  AiAssistantConfig,
  AiChatResult,
  AiClassification,
  AiDataCategory,
  AiDocSegment,
  AiDocumentSpec,
} from "../scenario/types";
import { audio } from "../lib/audio";
import { Spotlight } from "../ui/Spotlight";
import { AiSecurityBriefing } from "./AiSecurityBriefing";
import { SkipButton } from "../ui/SkipButton";
import { FileReviewPanel } from "./ai-assistant/FileReview";
import { Ico, type IcoName } from "./ai-assistant/icons";
import { ResultReport } from "./ai-assistant/ResultReport";
import { buildReview, type FileDecision, type ReviewData, type ShareMethod } from "./ai-assistant/review";

/**
 * Episode 6 ("The AI assistant dilemma") -- a realistic ChatGPT-in-the-browser
 * simulation. Priya asks (over Teams) for a summary of the confidential
 * NorthBay/Horizon contract and the learner works through the tool:
 *   1. Account  -- choose which account the chat is signed into. The learner
 *                  works for the company, so a personal AI account is a serious
 *                  risk; the managed company workspace is the safe choice.
 *   2. Files    -- review the files ONE AT A TIME and decide, for each, whether
 *                  it is safe to upload: send as-is, redact it first, or do not
 *                  attach it. Every file must be decided.
 *   3. Check    -- "Check your choices": a recap of exactly what will be sent.
 *                  Only its Send button sends; the request then plays out on the
 *                  chat screen (files go up, the assistant analyses them, the
 *                  draft appears) with no extra clicking.
 *   4. Share    -- the climax: how the summary reaches Priya, including the real,
 *                  high-risk option of a PUBLIC share link.
 * A report follows (the learner's choices next to the safe choices, a redaction
 * check, what left the company), then the "how a public link escapes" animation
 * on EVERY path. Scoring is hidden + weighted and unchanged: nothing reveals the
 * answer before the learner commits, and `evaluate()` is the only thing that
 * decides the tier / events / routing.
 *
 * A "Skip exercise" control sits in the browser chrome bar throughout (see
 * `skipExercise`) -- since no real decision was made, it bypasses scoring
 * entirely (no eventIds, no report) rather than fabricating a good or bad outcome.
 */

/** Inert decoy bookmarks so the bar reads as a real browser's, not a single
 *  suspicious button. Only the ChatGPT bookmark (rendered separately) is
 *  wired up + blinking. */
const DECOY_BOOKMARKS: { label: string; icon: string }[] = [
  { label: "Webmail", icon: "\u2709" },
  { label: "Intranet", icon: "\u2302" },
  { label: "Timesheet", icon: "\u23F1" },
];

type Phase = "home" | "compose" | "reply" | "result" | "linkescape";
type AiIntroStep = 0 | 1 | 2 | 3;

function initialsOf(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** Categories that should never leave the company through this request. */
const PROTECTED_CATEGORIES: AiDataCategory[] = [
  "personal",
  "customer_identity",
  "financial",
  "credential",
  "signature",
];

const CONFIDENTIAL_LEVELS: AiClassification[] = ["Confidential", "Strictly confidential"];

const SHARE_LABEL: Record<ShareMethod, string> = {
  public_link: "Created a public share link and sent the URL",
  copy_text: "Copied the summary text into the reply",
  internal_channel: "Wrote the key points in an internal reply",
  guidance: "Paused and asked for guidance",
};

const SHARE_ICON: Record<ShareMethod, IcoName> = {
  public_link: "link",
  copy_text: "copy",
  internal_channel: "chat",
  guidance: "shield",
};

/** What each way of sharing lets other people see, shown under every option. */
const SHARE_LEAVES: Record<ShareMethod, { text: string; tone: "bad" | "ok" }[]> = {
  public_link: [
    { text: "The whole chat", tone: "bad" },
    { text: "Every attached file", tone: "bad" },
    { text: "Anyone who gets the URL", tone: "bad" },
  ],
  copy_text: [
    { text: "Only the summary text", tone: "ok" },
    { text: "Chat and files stay behind", tone: "ok" },
  ],
  internal_channel: [
    { text: "Only the summary text", tone: "ok" },
    { text: "Stays inside the company", tone: "ok" },
  ],
  guidance: [{ text: "Nothing is sent yet", tone: "ok" }],
};

/** Draft the assistant writes. The full draft needs at least one line the task
 *  needs to be readable in an attached file; otherwise it can only say so. */
const DRAFT_FULL = {
  lead: "Here is a draft summary for the steering committee:",
  bullets: [
    "Key obligations: managed data services, quarterly reporting and a 99.9% service level.",
    "Exit conditions: either party may exit on 90 days' written notice (clause 14.3).",
    "Financial exposure: CHF 4.2M contract value with an equal early-exit penalty.",
  ],
};
const DRAFT_THIN = {
  lead: "Here is what I can say from the files you attached:",
  bullets: [
    "They describe a managed-services partnership in the health sector, at a high level.",
    "They do not include the key obligations, exit conditions or financial exposure that were asked for.",
    "I would need the contract clauses themselves to write that part of the summary.",
  ],
};

export function AiAssistant({
  config,
  onEnded,
  onStageChange,
}: {
  config: AiAssistantConfig;
  onEnded: (result: AiChatResult) => void;
  /** Reports a coarse internal stage so the persistent top-right guide
   *  (App.tsx) can show contextual "what to do next" copy -- same pattern as
   *  PersonalLaptop's onStageChange. */
  onStageChange?: (stage: string) => void;
}) {
  const appName = config.appName ?? "ChatGPT";
  const modelName = config.modelName ?? "GPT-5";
  const chatUrl = config.chatUrl ?? "chatgpt.com";
  const requesterName = config.requesterName ?? "Priya Anand";
  const requesterFirst = requesterName.split(" ")[0] ?? "Priya";
  const companyName = config.companyName ?? "your company";
  const companyShort = config.companyName?.split(" ")[0] ?? "the company";
  const corpWs = config.workspaces.find((w) => w.kind === "corporate");
  const corpLabel = corpWs?.label ?? "the company's managed workspace";
  const docs = config.documents;

  const [collapsed, setCollapsed] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  // True once the learner has explicitly picked an account in the menu
  // (picking the one already selected counts). Gates the paperclip, so the
  // account decision always comes first and is never skipped by accident.
  const [accountChosen, setAccountChosen] = useState(false);
  const [workspaceId, setWorkspaceId] = useState(config.defaultWorkspaceId);
  const [docPanelOpen, setDocPanelOpen] = useState(false);
  const [panelView, setPanelView] = useState<"files" | "review">("files");
  // An earlier (already decided) file the learner re-opened; null = the file they are on.
  const [viewId, setViewId] = useState<string | null>(null);
  // A one-click decision (send as-is / do not attach) that is being saved.
  const [pending, setPending] = useState<{ id: string; kind: FileDecision } | null>(null);
  const [attached, setAttached] = useState<string[]>([]);
  const [redactions, setRedactions] = useState<Record<string, Set<string>>>({});
  // "yes" / "skip" are committed decisions; "no" (redact first) is a draft until
  // the redacted copy is attached. No key = undecided.
  const [sendAsIs, setSendAsIs] = useState<Record<string, FileDecision>>({});
  const [prompt, setPrompt] = useState(config.prefilledPrompt ?? "");
  // Up-front tutorial, shown once when the chat first opens. Each card is tied
  // to what the learner is actually doing (see the effects below), so a card
  // never lingers over a screen that no longer matches it.
  const [introStep, setIntroStep] = useState<AiIntroStep | null>(null);
  const introStarted = useRef(false);
  // Optional illustrated "AI security basics" reading offered as the last
  // tutorial card -- see AiSecurityBriefing.tsx. Purely read-and-close.
  const [showAiBriefing, setShowAiBriefing] = useState(false);
  // Brief confirmation flash after switching accounts, so "did that actually
  // register?" is never a question.
  const [justSwitchedTo, setJustSwitchedTo] = useState<string | null>(null);
  // Shown when the locked paperclip is clicked before an account is chosen.
  const [lockNudge, setLockNudge] = useState(false);

  // Spotlight targets -- see ui/Spotlight.tsx.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const acctTargetRef = useRef<HTMLDivElement | null>(null);
  const attachBtnRef = useRef<HTMLButtonElement | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const noTargetRef = useRef<HTMLElement | null>(null);
  // Start on a plain browser "new tab" page (bookmarks bar, blinking ChatGPT
  // bookmark) rather than dropping the learner straight into an already-open
  // chat -- mirrors how the desktop -> Chrome -> site flow works everywhere
  // else in this course (see b_desktop_chatgpt in the scenario).
  const [phase, setPhase] = useState<Phase>("home");
  const [shareOpen, setShareOpen] = useState(false);
  const [pendingLink, setPendingLink] = useState(false);
  const [result, setResult] = useState<AiChatResult | null>(null);
  const [review, setReview] = useState<ReviewData | null>(null);
  // Priya's narrated request and the persistent "Task from Priya" card said
  // the SAME thing at the same time. Hold the card back until the voice/caption
  // is done so the learner reads it once, then keeps the card as a reference.
  const [taskRevealed, setTaskRevealed] = useState(false);
  const spoke = useRef(false);

  // The send animation: 0 files go up, 1 the assistant analyses them, 2 the
  // draft is written, 3 done (the Share bar appears).
  const [replyStage, setReplyStage] = useState<0 | 1 | 2 | 3>(0);
  const [upPct, setUpPct] = useState<number[]>([]);
  const [stepDone, setStepDone] = useState(0);
  const [typed, setTyped] = useState(0);
  const pendingTimer = useRef<number | null>(null);
  const animCleanup = useRef<() => void>(() => undefined);

  const workspace = useMemo(
    () => config.workspaces.find((w) => w.id === workspaceId) ?? config.workspaces[0],
    [config.workspaces, workspaceId]
  );

  const docById = (id: string): AiDocumentSpec | undefined => docs.find((d) => d.id === id);

  // --- per-file progress ----------------------------------------------------
  // A file counts as "decided" once it is attached (as-is, or a redacted copy)
  // or explicitly left out -- NOT merely opened, and NOT "redact it first" on
  // its own (that is still mid-redaction until the redacted copy is attached).
  const isDocDecided = (id: string) => attached.includes(id) || sendAsIs[id] === "skip";
  const undecidedDocs = docs.filter((d) => !isDocDecided(d.id));
  const allDocsDecided = undecidedDocs.length === 0;
  const decidedCount = docs.length - undecidedDocs.length;
  const currentId = undecidedDocs[0]?.id ?? null;
  const shownId = viewId && docById(viewId) ? viewId : currentId;
  const redactMode = !!shownId && !isDocDecided(shownId) && sendAsIs[shownId] === "no";

  const attachedCount = attached.length;
  const canSendNow = attachedCount > 0 && prompt.trim().length > 0;

  // Priya's request narration plays once the chat is actually open (not while
  // still browsing the new-tab/bookmarks screen) so the audio matches what's
  // on screen.
  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    if (config.requestMessage) void audio.prefetchSpeech(config.requestMessage, { voice: "onyx" });
    if (config.linkExposureIntro) void audio.prefetchSpeech(config.linkExposureIntro, { voice: "onyx" });
    if (phase !== "compose" || spoke.current) return;
    spoke.current = true;
    void audio.speak(config.requestMessage, { voice: "onyx" }).then(() => setTaskRevealed(true));
    return () => audio.cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    if (phase !== "compose" || introStarted.current) return;
    introStarted.current = true;
    setIntroStep(0);
  }, [phase]);

  // Tutorial cards follow what the learner actually does. Choosing an account
  // while card 1 is up moves on to the files card; opening the files window ends
  // the tutorial (that window carries its own guidance), so a card that names
  // the paperclip can never stay up over a screen that has already moved on.
  useEffect(() => {
    if (introStep === 0 && accountChosen) setIntroStep(1);
  }, [introStep, accountChosen]);
  useEffect(() => {
    if (docPanelOpen && introStep !== null) setIntroStep(null);
  }, [docPanelOpen, introStep]);

  // Clean up timers when the device unmounts.
  useEffect(
    () => () => {
      if (pendingTimer.current !== null) window.clearTimeout(pendingTimer.current);
      animCleanup.current();
    },
    []
  );

  const advanceIntro = () => {
    audio.click();
    setIntroStep((step) => (step === null || step === 3 ? null : ((step + 1) as AiIntroStep)));
  };
  const skipIntro = () => {
    audio.click();
    setIntroStep(null);
  };
  const openAiBriefing = () => {
    audio.click();
    // Hide the tutorial card BEFORE showing the full briefing modal --
    // otherwise the small "before you start" card keeps rendering on top of
    // the modal it just opened, covering the title/first section.
    setIntroStep(null);
    setShowAiBriefing(true);
  };
  const closeAiBriefing = () => {
    setShowAiBriefing(false);
    setIntroStep(null);
  };

  const switchWorkspace = (id: string) => {
    audio.click();
    setWorkspaceId(id);
    setAccountChosen(true);
    setProfileOpen(false);
    const label = config.workspaces.find((w) => w.id === id)?.label ?? id;
    setJustSwitchedTo(label);
    window.setTimeout(() => setJustSwitchedTo((cur) => (cur === label ? null : cur)), 2200);
  };

  const closeProfile = () => setProfileOpen(false);

  // --- file decisions -------------------------------------------------------

  const clearPendingTimer = () => {
    if (pendingTimer.current !== null) {
      window.clearTimeout(pendingTimer.current);
      pendingTimer.current = null;
    }
  };

  const chooseDecision = (id: string, kind: FileDecision) => {
    if (pending) return;
    audio.click();
    if (kind === "no") {
      // Redact first: not a decision until the redacted copy is attached.
      setSendAsIs((prev) => ({ ...prev, [id]: "no" }));
      setAttached((prev) => prev.filter((x) => x !== id));
      return;
    }
    // Send as-is / do not attach: one click. A short "saved" beat, then on to the next file.
    setSendAsIs((prev) => ({ ...prev, [id]: kind }));
    setPending({ id, kind });
    pendingTimer.current = window.setTimeout(() => {
      pendingTimer.current = null;
      setAttached((prev) => (kind === "yes" ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id)));
      setRedactions((prev) => ({ ...prev, [id]: new Set<string>() }));
      setPending(null);
      setViewId(null);
    }, 520);
  };

  const toggleRedact = (docId: string, segId: string) => {
    audio.click();
    setRedactions((prev) => {
      const next = { ...prev };
      const set = new Set(next[docId] ?? []);
      if (set.has(segId)) set.delete(segId);
      else set.add(segId);
      next[docId] = set;
      return next;
    });
  };

  const attachRedacted = (id: string) => {
    if ((redactions[id]?.size ?? 0) === 0) return;
    audio.click();
    setAttached((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setViewId(null);
  };

  const undecide = (id: string) => {
    setAttached((prev) => prev.filter((x) => x !== id));
    setSendAsIs((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const reopenDoc = (id: string) => {
    audio.click();
    clearPendingTimer();
    setPending(null);
    undecide(id);
    setViewId(id);
    setPanelView("files");
    setDocPanelOpen(true);
  };

  const removeAttach = (id: string) => {
    audio.click();
    undecide(id);
  };

  const viewDoc = (id: string) => {
    audio.click();
    setViewId(id === currentId ? null : id);
  };

  const openDocPanel = () => {
    if (!accountChosen) {
      // The paperclip is locked until an account is chosen.
      audio.errorBuzz();
      setLockNudge(true);
      window.setTimeout(() => setLockNudge(false), 2400);
      return;
    }
    audio.click();
    setPanelView("files");
    setViewId(null);
    setDocPanelOpen(true);
  };

  const closeDocPanel = () => {
    audio.click();
    setDocPanelOpen(false);
    setPanelView("files");
  };

  const goReview = () => {
    audio.click();
    setPanelView("review");
    setDocPanelOpen(true);
  };

  // --- scoring ---------------------------------------------------------------

  const disclosedSegments = useMemo(() => {
    const out: { doc: AiDocumentSpec; seg: AiDocSegment }[] = [];
    for (const id of attached) {
      const doc = docs.find((d) => d.id === id);
      if (!doc) continue;
      const red = redactions[id];
      for (const seg of doc.segments) {
        if (!seg.category) continue;
        if (doc.redactable && red?.has(seg.id)) continue;
        out.push({ doc, seg });
      }
    }
    return out;
  }, [attached, redactions, docs]);

  const evaluate = (shareMethod: ShareMethod, preSend: boolean): AiChatResult => {
    const ws = workspace;

    // Nothing was ever sent to the AI (paused before sending) -> clean.
    if (preSend) {
      return {
        tier: "safe_alt",
        outcomeKey: "safe",
        eventIds: ["ai_requested_guidance"],
        workspaceLabel: "None \u2014 you paused and asked for guidance",
        workspaceKind: "none",
        attachedDocNames: [],
        disclosed: [],
        concerns: [],
        goodCalls: [],
        shareMethod: "guidance",
        shareLabel: SHARE_LABEL.guidance,
        sharedPublicLink: false,
        summaryLine:
          "You stopped before sending anything and asked the right team for guidance. Slower, but nothing confidential left the company.",
        recommendation:
          "When a request touches confidential customer material and the rules aren't clear, pausing to ask is always a valid answer.",
      };
    }

    const catMap = new Map<AiDataCategory, boolean>();
    for (const { seg } of disclosedSegments) {
      const cat = seg.category!;
      const prev = catMap.get(cat);
      const necessary = !!seg.neededForTask;
      catMap.set(cat, prev === undefined ? necessary : prev && necessary);
    }
    const disclosed = [...catMap.entries()].map(([category, necessary]) => ({ category, necessary }));

    const unnecessaryCount = disclosedSegments.filter((d) => !d.seg.neededForTask).length;
    const credentialLeaked = catMap.has("credential");
    const personalLeaked = catMap.has("personal");
    const financialLeaked = catMap.has("financial");

    const attachedDocs = attached.map(docById).filter(Boolean) as AiDocumentSpec[];
    const attachedNames = attachedDocs.map((d) => d.name);

    const originalDoc = attachedDocs.find((d) => d.redactable);
    const attachedOriginalFull =
      !!originalDoc &&
      originalDoc.segments.some(
        (s) =>
          s.category &&
          PROTECTED_CATEGORIES.includes(s.category) &&
          !redactions[originalDoc.id]?.has(s.id)
      );

    const steeringDoc = attachedDocs.find((d) => d.classification === "Strictly confidential");
    const attachedSteering = !!steeringDoc;
    const anySufficient = attachedDocs.some((d) => d.sufficientForTask);
    const anyConfidentialDoc = attachedDocs.some((d) => CONFIDENTIAL_LEVELS.includes(d.classification));
    const minimalSufficient = anySufficient && unnecessaryCount === 0;

    const publicLink = shareMethod === "public_link";

    // ---- sub-scores (0..1) ----
    const wsScore = ws.kind === "corporate" ? 1 : ws.kind === "shared" ? 0.4 : 0;
    let docScore: number;
    if (attachedSteering) docScore = 0.1;
    else if (minimalSufficient) docScore = 1;
    else if (attachedOriginalFull) docScore = 0.4;
    else if (attachedDocs.length && !anySufficient) docScore = 0.3;
    else if (!attachedDocs.length) docScore = 0.15;
    else docScore = 0.6;
    const minScore = unnecessaryCount === 0 ? 1 : unnecessaryCount <= 2 ? 0.6 : unnecessaryCount <= 4 ? 0.3 : 0.1;
    const protectedLeft = PROTECTED_CATEGORIES.filter((c) => !catMap.has(c)).length;
    const protScore = protectedLeft / PROTECTED_CATEGORIES.length;
    const shareScore =
      shareMethod === "internal_channel" ? 1 : shareMethod === "guidance" ? 0.85 : shareMethod === "copy_text" ? 0.7 : 0;

    const total = 0.3 * wsScore + 0.25 * docScore + 0.2 * minScore + 0.15 * protScore + 0.1 * shareScore;

    // ---- tier ----
    let tier: AiChatResult["tier"];
    if (credentialLeaked) tier = "critical";
    else if (ws.kind === "personal" && attachedOriginalFull) tier = "critical";
    else if (ws.kind === "shared" && anyConfidentialDoc) tier = "critical";
    else if (attachedSteering && ws.kind !== "corporate") tier = "critical";
    else if (publicLink && (anyConfidentialDoc || disclosed.length > 0)) tier = "critical";
    else if (publicLink) tier = "high";
    else if (attachedSteering) tier = "high";
    else if (total >= 0.85 && ws.kind === "corporate" && unnecessaryCount === 0) tier = "best";
    else if (total >= 0.6) tier = "partial";
    else tier = "high";

    // ---- routing key ----
    let outcomeKey: AiChatResult["outcomeKey"];
    if (tier === "best") outcomeKey = "safe";
    else if (ws.kind === "shared") outcomeKey = "shared";
    else outcomeKey = "risky";

    // ---- events ----
    const eventIds: string[] = [];
    if (ws.kind === "corporate") eventIds.push("ai_used_approved_workspace");
    if (ws.kind === "personal") eventIds.push("ai_used_personal_workspace");
    if (ws.kind === "shared") eventIds.push("ai_used_shared_login");
    if (credentialLeaked) eventIds.push("ai_leaked_credential");
    if (personalLeaked) eventIds.push("ai_leaked_personal_data");
    if (financialLeaked) eventIds.push("ai_leaked_financial_data");
    if (attachedOriginalFull) eventIds.push("ai_uploaded_full_contract");
    if (attachedSteering) eventIds.push("ai_uploaded_unnecessary_confidential");
    if (minimalSufficient) eventIds.push("ai_minimised_data");
    if (attachedDocs.length && !anySufficient) eventIds.push("ai_used_insufficient_public_info");
    if (publicLink) eventIds.push("ai_shared_public_link");
    else if (shareMethod === "copy_text" || shareMethod === "internal_channel")
      eventIds.push("ai_kept_result_internal");
    else if (shareMethod === "guidance") eventIds.push("ai_requested_guidance");

    // ---- concerns (plain language) ----
    const concerns: string[] = [];
    if (publicLink)
      concerns.push(
        "You created a public share link to the whole conversation. Anyone who ever gets that URL can open it \u2014 no login, no expiry, and no way to know who has read it."
      );
    if (credentialLeaked)
      concerns.push("A system access code that works like a password was sent to an outside AI service.");
    if (ws.kind === "personal")
      concerns.push("You were signed in to a personal account your company doesn't manage or control.");
    if (ws.kind === "shared")
      concerns.push("You used a shared department login \u2014 anyone else using it can read this conversation later.");
    if (financialLeaked) concerns.push("Bank / payment details were included but aren't needed for a summary.");
    if (personalLeaked) concerns.push("A named person's contact details were included but weren't required.");
    if (attachedSteering)
      concerns.push("Strictly confidential steering-committee notes were attached that Priya never asked for.");
    if (attachedOriginalFull && !credentialLeaked)
      concerns.push("The complete original contract was uploaded when only a few clauses were needed.");
    if (!anySufficient && attachedDocs.length)
      concerns.push("The attached material is public and doesn't contain enough to complete Priya's summary.");

    // ---- good calls (plain language, positive) ----
    // A fully-redacted sensitive file already scores like a clean document
    // above (disclosedSegments excludes redacted lines from every leak
    // check) -- but nothing on the result screen said so, leaving the learner
    // to guess whether redacting actually "worked". Say it explicitly.
    const fullyRedactedSensitiveDocs = attachedDocs.filter((d) => {
      if (!d.redactable) return false;
      const protectedSegs = d.segments.filter((s) => s.category && PROTECTED_CATEGORIES.includes(s.category));
      if (!protectedSegs.length) return false;
      const red = redactions[d.id];
      return protectedSegs.every((s) => red?.has(s.id));
    });
    const goodCalls: string[] = [];
    if (fullyRedactedSensitiveDocs.length)
      goodCalls.push(
        `You attached ${fullyRedactedSensitiveDocs.length === 1 ? "a sensitive file" : "sensitive files"} (${fullyRedactedSensitiveDocs
          .map((d) => d.name)
          .join(", ")}) but blacked out everything sensitive in it first \u2014 that did not count against you.`
      );

    return {
      tier,
      outcomeKey,
      eventIds,
      workspaceLabel: ws.label,
      workspaceKind: ws.kind,
      attachedDocNames: attachedNames,
      disclosed,
      concerns,
      goodCalls,
      shareMethod,
      shareLabel: SHARE_LABEL[shareMethod],
      sharedPublicLink: publicLink,
      summaryLine: buildSummary(tier, ws.label, attachedNames, unnecessaryCount, publicLink),
      recommendation: buildRecommendation(config, tier, publicLink),
    };
  };

  const finalize = (r: AiChatResult) => {
    if (r.tier === "best" || r.tier === "safe_alt") audio.chimeGood();
    else if (r.tier === "critical") audio.errorBuzz();
    else audio.chimeBad();
    setReview(
      buildReview({
        docs,
        attached,
        redactions,
        workspace,
        workspaces: config.workspaces,
        shareMethod: r.shareMethod,
        appName,
      })
    );
    setResult(r);
    setShareOpen(false);
    setPendingLink(false);
    setPhase("result");
  };

  const commitShare = (method: ShareMethod) => {
    audio.click();
    if (method === "public_link" && !pendingLink) {
      // realistic two-step: show the generated public URL + neutral note first.
      setPendingLink(true);
      return;
    }
    finalize(evaluate(method, false));
  };

  const cancelAndAsk = () => {
    audio.click();
    finalize(evaluate("guidance", true));
  };

  // "Skip this exercise" -- unlike every other exit path here, this is NOT
  // scored: no workspace/attach/redact/share decision was actually made, so
  // recording any of those events (or routing through the report, which
  // exists to explain a real decision) would misrepresent what happened.
  // Bypasses straight to the "safe" edge (the only routing option that
  // doesn't trigger a negative consequence scene) with an empty event list --
  // same "don't fabricate a score" philosophy as PrivacyHunt3D's "Skip
  // straight to sorting".
  const skipExercise = () => {
    audio.cancelSpeech();
    audio.click();
    onEnded({
      tier: "safe_alt",
      outcomeKey: "safe",
      eventIds: [],
      workspaceLabel: "Skipped \u2014 no decision made",
      workspaceKind: "none",
      attachedDocNames: [],
      disclosed: [],
      concerns: [],
      goodCalls: [],
      shareMethod: "none",
      shareLabel: "Skipped",
      sharedPublicLink: false,
      summaryLine: "You skipped this exercise before making any decisions.",
      recommendation:
        "Whose account, what data, and how a result leaves the company are the three questions to ask before AI touches company data.",
    });
  };

  // --- the send animation ----------------------------------------------------

  // What the assistant can actually write depends on what is readable in the
  // files that were sent: with none of the lines the task needs, it can only say so.
  const draft = useMemo(() => {
    const neededReadable = attached.some((id) => {
      const d = docs.find((x) => x.id === id);
      return !!d?.segments.some((s) => s.neededForTask && !redactions[id]?.has(s.id));
    });
    return neededReadable ? DRAFT_FULL : DRAFT_THIN;
  }, [attached, redactions, docs]);
  const draftLines = useMemo(() => [draft.lead, ...draft.bullets], [draft]);
  const draftLen = useMemo(() => draftLines.join("\n").length, [draftLines]);

  const analyseSteps = useMemo(
    () => [
      ...attached.map((id) => `Reading ${docs.find((d) => d.id === id)?.name ?? "a file"}`),
      "Finding the obligations, exit conditions and financial exposure",
      "Drafting the summary",
    ],
    [attached, docs]
  );

  const startSend = () => {
    if (!canSendNow) return;
    audio.click();
    setDocPanelOpen(false);
    setPanelView("files");
    setShareOpen(false);
    setReplyStage(0);
    setStepDone(0);
    setTyped(0);
    setUpPct(attached.map(() => 0));
    setPhase("reply");
  };

  useEffect(() => {
    if (phase !== "reply") return;
    const timers: number[] = [];
    let iv: number | null = null;
    const at = (ms: number, fn: () => void) => {
      timers.push(window.setTimeout(fn, ms));
    };
    const n = attached.length;
    const steps = analyseSteps.length;
    audio.typing(5);

    // 0: the files go up, one after another.
    attached.forEach((_, i) =>
      at(260 + i * 300, () => setUpPct((prev) => prev.map((v, k) => (k === i ? 100 : v))))
    );
    const uploadsDone = 260 + Math.max(0, n - 1) * 300 + 700;
    at(uploadsDone, () => {
      audio.dataBlip();
      setReplyStage(1);
    });
    // 1: the assistant reads each file, then works out the answer.
    const stepMs = 430;
    for (let k = 1; k <= steps; k++) at(uploadsDone + k * stepMs, () => setStepDone(k));
    const writeAt = uploadsDone + steps * stepMs + 260;
    // 2: the draft is written out.
    at(writeAt, () => {
      setReplyStage(2);
      let c = 0;
      iv = window.setInterval(() => {
        c = Math.min(draftLen, c + 5);
        setTyped(c);
        if (c >= draftLen) {
          if (iv !== null) window.clearInterval(iv);
          iv = null;
          audio.ui("message");
          setReplyStage(3);
        }
      }, 28);
    });

    const stop = () => {
      timers.forEach((t) => window.clearTimeout(t));
      if (iv !== null) window.clearInterval(iv);
      iv = null;
    };
    animCleanup.current = stop;
    return stop;
    // The animation is fixed once the request goes out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const skipAnimation = () => {
    audio.click();
    animCleanup.current();
    setUpPct(attached.map(() => 100));
    setStepDone(analyseSteps.length);
    setTyped(draftLen);
    setReplyStage(3);
  };

  // Keep the newest line of the conversation in view while it grows.
  useEffect(() => {
    if (phase !== "reply") return;
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [phase, replyStage, stepDone, typed]);

  // --- stage + guidance --------------------------------------------------------

  const introVisible = phase === "compose" && introStep !== null && !profileOpen && !docPanelOpen && !showAiBriefing;

  const stage = showAiBriefing
    ? "briefing"
    : phase === "home"
    ? "home"
    : phase === "result"
    ? "result"
    : phase === "linkescape"
    ? "linkescape"
    : phase === "reply"
    ? shareOpen
      ? "share"
      : replyStage < 3
      ? "sending"
      : "reply"
    : profileOpen
    ? "account_menu"
    : introVisible
    ? "intro"
    : docPanelOpen
    ? panelView === "review"
      ? "review"
      : allDocsDecided
      ? "docs_ready"
      : redactMode
      ? "docs_redacting"
      : "docs_deciding"
    : !accountChosen
    ? "account"
    : !allDocsDecided
    ? "attach"
    : "docs_ready";

  useEffect(() => {
    onStageChange?.(stage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // The spotlight dims everything except the one control to use next. It never
  // fights the files window / share sheet / account menu (they already darken
  // the screen themselves) and it is off while a tutorial card is elsewhere.
  const spotTarget: RefObject<HTMLElement | null> | null =
    phase !== "compose" || profileOpen || docPanelOpen || showAiBriefing || shareOpen
      ? null
      : introStep !== null
      ? introStep === 0
        ? acctTargetRef
        : introStep === 1
        ? attachBtnRef
        : null
      : stage === "account"
      ? acctTargetRef
      : stage === "attach"
      ? attachBtnRef
      : null;
  // Always exactly one entry, so the array handed to Spotlight never changes size.
  const spotlightTargets: RefObject<HTMLElement | null>[] = [spotTarget ?? noTargetRef];

  // ------------------------------------------------------------------------

  if (phase === "result" && result && review) {
    return (
      <ResultReport
        result={result}
        review={review}
        onSeeEscape={() => {
          audio.click();
          setPhase("linkescape");
        }}
        onContinue={() => onEnded(result)}
      />
    );
  }

  if (phase === "linkescape" && result) {
    return <LinkEscape config={config} onContinue={() => onEnded(result)} />;
  }

  if (phase === "home") {
    return (
      <div className="winapp cgpt-browser">
        <div className="cgpt-chrome">
          <span className="cgpt-dot cgpt-dot--r" aria-hidden />
          <span className="cgpt-dot cgpt-dot--y" aria-hidden />
          <span className="cgpt-dot cgpt-dot--g" aria-hidden />
          <div className="cgpt-omni" aria-hidden>
            <span className="cgpt-omni-url">New Tab</span>
          </div>
          <SkipButton className="cf-skip-btn--compact" label="Skip exercise" onClick={skipExercise} />
        </div>
        <div className="cgpt-bookbar" role="toolbar" aria-label="Bookmarks">
          {DECOY_BOOKMARKS.map((b) => (
            <span key={b.label} className="cgpt-bookmark" aria-hidden>
              <span className="cgpt-bookmark-ico">{b.icon}</span>
              {b.label}
            </span>
          ))}
          <button
            type="button"
            className="cgpt-bookmark hot cf-blink-strong"
            onClick={() => {
              audio.click();
              setPhase("compose");
            }}
          >
            <span className="cgpt-bookmark-ico" aria-hidden>
              {"\u2726"}
            </span>
            {appName}
          </button>
        </div>
        <div className="cgpt-newtab">
          <span className="cgpt-newtab-logo" aria-hidden>
            {"\u25A2"}
          </span>
          {/* The whole hint (not just the bookmark button above) is clickable --
              the arrow used to be purely decorative, so a learner who tried to
              click IT (a natural instinct) got nothing. */}
          <button
            type="button"
            className="cgpt-newtab-hint cf-pulse-target"
            onClick={() => {
              audio.click();
              setPhase("compose");
            }}
          >
            <span className="cgpt-newtab-arrow" aria-hidden>
              {"\u2191"}
            </span>
            <p>
              {requesterFirst} just messaged you on Teams asking for a contract summary. Click the {appName} bookmark above
              to open it.
            </p>
          </button>
        </div>
      </div>
    );
  }

  // --- copy that depends on the learner's progress -----------------------------

  const introCopy: Record<AiIntroStep, { n: string; title: string; body: string }> = {
    0: {
      n: "Step 1 of 3",
      title: `Pick ${corpLabel}`,
      body: `You work for ${companyName}, so company work belongs in ${companyShort}'s managed workspace. A personal AI account is not managed by the company, which is a serious risk. Click your account at the top right of the chat.`,
    },
    1: {
      n: "Step 2 of 3",
      title: "Review your files",
      body: `${requesterFirst} wants the contract summarised. You have been asked to decide, for each file, whether it is safe to upload. That is why there is a paperclip, at the bottom left of the chat box. Click it to open your files.${
        accountChosen ? "" : " It unlocks once you have chosen your account."
      }`,
    },
    2: {
      n: "Step 3 of 3",
      title: "Decide, check, share",
      body: `You decide every file, one at a time: send it as-is, redact it first, or do not attach it. Then a Check your choices button lets you look over what will be sent. After that the request goes out for you, and your last decision is how to share the summary with ${requesterFirst}.`,
    },
    3: {
      n: "Before you start",
      title: "Optional refresher",
      body: "One more thing, totally optional: putting company data into an AI tool carries its own risks \u2014 data exposure, oversharing through a public link, and rules about what's even allowed to leave the company. Want a 2-minute illustrated refresher first?",
    },
  };

  const coach: { n: string; title: string; body: string; action?: { label: string; onClick: () => void } } | null =
    introVisible || showAiBriefing || docPanelOpen || profileOpen || shareOpen
      ? null
      : phase === "reply"
      ? null
      : !accountChosen
      ? {
          n: "Step 1 of 3",
          title: "Choose your account",
          body: `You work for ${companyName}, so use ${corpLabel}. A personal AI account is not managed by the company, which makes it a serious risk for company data. Click your account at the top right.`,
        }
      : !allDocsDecided
      ? {
          n: "Step 2 of 3",
          title: "Review your files",
          body:
            decidedCount === 0
              ? `${requesterFirst} wants the contract summarised, so decide for each file whether it is safe to upload. Click the paperclip at the bottom left of the chat box.`
              : `${decidedCount} of ${docs.length} files decided. Click the paperclip at the bottom left of the chat box to carry on with the next file.`,
        }
      : {
          n: "Step 3 of 3",
          title: "Check your choices",
          body: "Every file now has a decision. Next step: Check your choices, so you can look over what will be sent before anything is sent.",
          action: { label: "Check your choices", onClick: goReview },
        };

  const statusNote = !accountChosen
    ? "Next: choose your account (top right)."
    : !allDocsDecided
    ? decidedCount === 0
      ? "Next: click the paperclip (bottom left)."
      : `${decidedCount} of ${docs.length} files decided. Click the paperclip.`
    : "All files decided. Next: Check your choices.";

  const acctStep = stage === "account" || (introVisible && introStep === 0);
  const attachStep = stage === "attach" || (introVisible && introStep === 1);
  const attachLocked = !accountChosen;

  const renderChip = (id: string) => {
    const d = docById(id);
    if (!d) return null;
    const redCount = redactions[id]?.size ?? 0;
    return (
      <span key={id} className="cgpt-chip cgx-chip-file">
        <span className={`cgx-ftype cgx-ftype--${d.fileType}`}>{d.fileType.toUpperCase()}</span>
        <span className="cgpt-chip-name">
          {d.name}
          {redCount > 0 && <span className="cgpt-chip-red"> {"\u00B7"} {redCount} redacted</span>}
        </span>
        <button type="button" className="cgpt-chip-x" aria-label={`Remove ${d.name}`} onClick={() => removeAttach(id)}>
          <Ico name="close" />
        </button>
      </span>
    );
  };

  return (
    <div className="winapp cgpt-browser cgx-root" ref={rootRef}>
      <Spotlight containerRef={rootRef} targets={spotlightTargets} active={spotTarget !== null} />
      {introVisible && introStep !== null && (
        <div className={`cgx-tour ${introStep === 3 ? "cgx-tour--offer" : ""}`} role="status">
          <span className="cgx-tour-step">{introCopy[introStep].n}</span>
          <strong className="cgx-tour-title">{introCopy[introStep].title}</strong>
          <p className="cgx-tour-body">{introCopy[introStep].body}</p>
          <div className="cgx-tour-actions">
            {introStep === 3 ? (
              <>
                <button type="button" className="cgx-btn cgx-btn--primary cgx-btn--sm" onClick={openAiBriefing}>
                  Read the briefing
                </button>
                <button type="button" className="cgx-btn cgx-btn--ghost cgx-btn--sm" onClick={skipIntro}>
                  Skip, start the task
                </button>
              </>
            ) : (
              <>
                <button type="button" className="cgx-btn cgx-btn--primary cgx-btn--sm" onClick={advanceIntro}>
                  Got it, next
                </button>
                <button type="button" className="cgx-btn cgx-btn--ghost cgx-btn--sm" onClick={skipIntro}>
                  Skip tutorial
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {showAiBriefing && <AiSecurityBriefing onClose={closeAiBriefing} />}
      <div className="cgpt-chrome">
        <span className="cgpt-dot cgpt-dot--r" aria-hidden />
        <span className="cgpt-dot cgpt-dot--y" aria-hidden />
        <span className="cgpt-dot cgpt-dot--g" aria-hidden />
        <div className="cgpt-omni" aria-hidden>
          <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden focusable="false">
            <path d="M12 3a9 9 0 100 18 9 9 0 000-18zm0 0v18M3 12h18" fill="none" stroke="currentColor" strokeWidth="1.4" />
          </svg>
          <span className="cgpt-omni-url">{chatUrl}/c/northbay-summary</span>
        </div>
        <SkipButton className="cf-skip-btn--compact" label="Skip exercise" onClick={skipExercise} />
      </div>

      <div className={`cgpt ${collapsed ? "cgpt--collapsed" : ""}`}>
        {/* Left sidebar */}
        <aside className="cgpt-side" aria-label="Chat history">
          <div className="cgpt-side-top">
            <button
              type="button"
              className="cgpt-collapse"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              onClick={() => {
                audio.click();
                setCollapsed((c) => !c);
              }}
            >
              {"\u2630"}
            </button>
            {!collapsed && (
              <button type="button" className="cgpt-newchat" onClick={() => audio.click()}>
                <span aria-hidden>{"\uFF0B"}</span> New chat
              </button>
            )}
          </div>
          {!collapsed && (
            <>
              <div className="cgpt-side-scroll">
                <div className="cgpt-side-label">Recent</div>
                {(config.sidebarHistory ?? []).map((t, i) => (
                  <div key={i} className={`cgpt-side-item ${i === 0 ? "active" : ""}`}>
                    {t}
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="cgpt-side-acct"
                onClick={() => {
                  audio.click();
                  setProfileOpen((o) => !o);
                }}
                aria-haspopup="menu"
                aria-expanded={profileOpen}
              >
                <span className={`cgpt-acct-avatar cgpt-acct-avatar--${workspace.kind}`} aria-hidden>
                  {initialsOf(workspace.label)}
                </span>
                <span className="cgpt-acct-meta">
                  <span className="cgpt-acct-name">{workspace.label}</span>
                  <span className="cgpt-acct-desc">{workspace.description}</span>
                </span>
                <span className="cgpt-acct-caret" aria-hidden>
                  {"\u2304"}
                </span>
              </button>
            </>
          )}
        </aside>

        {/* Main column */}
        <section className="cgpt-main">
          <header className="cgpt-topbar">
            <div className="cgpt-model">
              {modelName}
              <span className="cgpt-model-caret" aria-hidden>
                {"\u2304"}
              </span>
            </div>
            <div
              ref={acctTargetRef}
              className={`cgpt-acct-target ${phase === "compose" && acctStep && !accountChosen ? "cgpt-acct-target--active" : ""}`}
            >
              <button
                type="button"
                className="cgpt-ws-pill"
                onClick={() => {
                  audio.click();
                  setProfileOpen((o) => !o);
                }}
                aria-haspopup="menu"
                aria-expanded={profileOpen}
              >
                <span className={`cgpt-ws-dot cgpt-ws-dot--${workspace.kind}`} aria-hidden />
                {workspace.label}
              </button>
              <button
                type="button"
                className={`cgpt-avatar cgpt-avatar--${workspace.kind}`}
                aria-label="Account and workspace"
                onClick={() => {
                  audio.click();
                  setProfileOpen((o) => !o);
                }}
              >
                {initialsOf(workspace.label)}
              </button>
              {phase === "compose" && acctStep && !accountChosen && !profileOpen && (
                <span className="cgpt-acct-clickhere" aria-hidden>
                  Click here {"\u2192"}
                </span>
              )}
            </div>
          </header>

          {justSwitchedTo && (
            <div className="cgpt-switch-toast" role="status">
              <Ico name="check" /> You&apos;re now signed in as {justSwitchedTo}
            </div>
          )}

          {profileOpen && (
            <>
              <div className="cgpt-menu-scrim" onClick={closeProfile} aria-hidden />
              <div className="cgpt-menu cgx-menu cgx" role="menu" aria-label="Switch account or workspace">
                <div className="cgx-menu-brief">
                  <span className="cgx-menu-brief-ico" aria-hidden>
                    <Ico name="building" />
                  </span>
                  <div>
                    <strong>You work for {companyName}</strong>
                    <span>
                      Company work belongs in the company&apos;s managed workspace. A personal account is not managed by{" "}
                      {companyShort}: the company cannot see, audit or delete what you put in it, and it can be kept or
                      reused outside the company.
                    </span>
                  </div>
                </div>
                <div className="cgpt-menu-label">Choose an account</div>
                {config.workspaces.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={w.id === workspaceId}
                    className={`cgpt-menu-item cgx-menu-item cgx-menu-item--${w.kind} ${w.id === workspaceId ? "active" : ""}`}
                    onClick={() => switchWorkspace(w.id)}
                  >
                    <span className={`cgpt-menu-avatar cgpt-menu-avatar--${w.kind}`} aria-hidden>
                      {initialsOf(w.label)}
                    </span>
                    <span className="cgpt-menu-meta">
                      <span className="cgpt-menu-name">{w.label}</span>
                      <span className="cgpt-menu-desc">{w.description}</span>
                      <span className={`cgx-tag cgx-tag--${w.kind}`}>
                        {w.kind === "corporate"
                          ? `Use this for ${companyShort} work`
                          : w.kind === "personal"
                          ? "Not for company data"
                          : "Anyone with the login can read the chat"}
                      </span>
                    </span>
                    {w.id === workspaceId && (
                      <span className="cgpt-menu-check" aria-hidden>
                        <Ico name="check" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* One instruction strip, always naming the next control and where it
              sits. Hidden while a tutorial card, the AI briefing or the files
              window carries its own guidance. */}
          {coach && (
            <div className="cgpt-coach cgx-coach" role="status">
              <span className="cgpt-coach-step">{coach.n}</span>
              <span className="cgpt-coach-body">
                <strong>{coach.title}.</strong> {coach.body}
              </span>
              {coach.action && (
                <button type="button" className="cgx-btn cgx-btn--primary cgx-btn--sm cgx-btn--blink" onClick={coach.action.onClick}>
                  {coach.action.label}
                </button>
              )}
            </div>
          )}

          {/* conversation area */}
          <div className="cgpt-thread" ref={threadRef}>
            {phase === "compose" && (
              <div className="cgpt-context">
                <span className="cgpt-context-tag">Task from {requesterName}</span>
                {taskRevealed ? (
                  <p>{config.task}</p>
                ) : (
                  <p className="cgpt-context-listening">
                    <span className="cgpt-context-dot" aria-hidden /> listening to the request&hellip;
                  </p>
                )}
              </div>
            )}

            {phase === "compose" && (
              <div className="cgpt-empty">
                <div className="cgpt-empty-glyph" aria-hidden>
                  <Ico name="spark" />
                </div>
                <p>
                  {accountChosen
                    ? "Use the paperclip at the bottom left of the chat box to review your files first."
                    : "Choose your account first. Then use the paperclip to review your files."}
                </p>
              </div>
            )}

            {phase === "reply" && (
              <div className="cgpt-turns">
                <div className="cgpt-turn cgpt-turn--user">
                  <div className="cgpt-turn-body">
                    {prompt}
                    {attachedCount > 0 && (
                      <div className="cgx-sent-files">
                        {attached.map((id, i) => {
                          const d = docById(id);
                          if (!d) return null;
                          const pct = upPct[i] ?? 0;
                          const done = replyStage >= 1 || pct >= 100;
                          const hid = redactions[id]?.size ?? 0;
                          return (
                            <div key={id} className={`cgx-sent-file ${done ? "is-done" : ""}`}>
                              <span className={`cgx-ftype cgx-ftype--${d.fileType}`}>{d.fileType.toUpperCase()}</span>
                              <span className="cgx-sent-name">
                                {d.name}
                                {hid > 0 && <em> {"\u00B7"} {hid} line{hid === 1 ? "" : "s"} blacked out</em>}
                              </span>
                              <span className="cgx-sent-st">{done ? <><Ico name="check" /> Uploaded</> : "Uploading"}</span>
                              <span className="cgx-sent-bar" aria-hidden>
                                <i style={{ width: `${done ? 100 : pct}%` }} />
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {replyStage >= 1 && (
                  <div className="cgpt-turn cgpt-turn--ai">
                    <span className="cgpt-turn-badge" aria-hidden>
                      <Ico name="spark" />
                    </span>
                    <div className="cgpt-turn-body">
                      {replyStage < 2 ? (
                        <div className="cgx-working" aria-label={`${appName} is working`}>
                          <ul className="cgx-steps">
                            {analyseSteps.map((s, i) =>
                              i >= stepDone - 1 && i <= stepDone ? (
                                <li key={s} className={i < stepDone ? "is-done" : "is-now"}>
                                  <span className="cgx-step-mark" aria-hidden>
                                    {i < stepDone ? <Ico name="check" /> : <i className="cgx-spin" />}
                                  </span>
                                  {s}
                                </li>
                              ) : null
                            )}
                          </ul>
                          <span className="cgx-working-bar" aria-hidden>
                            <i style={{ width: `${Math.round((Math.min(stepDone, analyseSteps.length) / analyseSteps.length) * 100)}%` }} />
                          </span>
                        </div>
                      ) : (
                        <>
                          <p className="cgx-thought">
                            <Ico name="check" /> Read {attachedCount} file{attachedCount === 1 ? "" : "s"} and wrote a draft
                          </p>
                          {(() => {
                            const shown = draftLines.join("\n").slice(0, typed).split("\n");
                            return (
                              <>
                                <p>{shown[0]}</p>
                                {shown.length > 1 && (
                                  <ul>
                                    {shown.slice(1).map((line, i) => (
                                      <li key={i}>{line}</li>
                                    ))}
                                  </ul>
                                )}
                              </>
                            );
                          })()}
                          {replyStage === 3 && (
                            <>
                              <p className="cgpt-ai-note">Anything else you&apos;d like me to adjust?</p>
                              <div className="cgx-ai-actions" aria-hidden>
                                <Ico name="copy" />
                                <Ico name="thumbup" />
                                <Ico name="thumbdown" />
                                <Ico name="share" />
                              </div>
                            </>
                          )}
                        </>
                      )}
                      {replyStage < 3 && (
                        <button type="button" className="cgx-skipanim" onClick={skipAnimation}>
                          Skip the wait
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* bottom of the chat: composer, the files-window dock, the disabled
              composer while the request runs, or the Share bar */}
          {phase === "compose" && !docPanelOpen && (
            <div className="cgpt-composer-wrap cgx">
              {attachedCount > 0 && <div className="cgpt-chips">{attached.map(renderChip)}</div>}
              <div className="cgx-composer">
                <button
                  type="button"
                  ref={attachBtnRef}
                  className={`cgx-attach ${attachLocked ? "is-locked" : ""} ${attachStep && !attachLocked ? "is-target" : ""}`}
                  aria-label="Attach files"
                  aria-disabled={attachLocked}
                  onClick={openDocPanel}
                >
                  {attachStep && !attachLocked && (
                    <span className="cgx-attach-hint" aria-hidden>
                      Click here to review your files
                    </span>
                  )}
                  {lockNudge && (
                    <span className="cgx-attach-hint cgx-attach-hint--warn" aria-hidden>
                      Choose your account first
                    </span>
                  )}
                  <Ico name={attachLocked ? "lock" : "paperclip"} />
                  <span>Attach files</span>
                </button>
                <textarea
                  className="cgpt-input"
                  value={prompt}
                  placeholder={config.promptPlaceholder ?? `Message ${appName}\u2026`}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={2}
                  aria-label="Prompt"
                />
                <button
                  type="button"
                  className="cgx-send"
                  aria-label="Check your choices before sending"
                  title={allDocsDecided ? "Check your choices before sending" : "Review your files first"}
                  disabled={!allDocsDecided || !accountChosen}
                  onClick={goReview}
                >
                  <Ico name="send" />
                </button>
              </div>
              <div className="cgpt-statusbar">
                <span className="cgpt-status-note">{statusNote}</span>
                <button type="button" className="cgpt-cancel" onClick={cancelAndAsk}>
                  Cancel and request guidance
                </button>
              </div>
            </div>
          )}

          {phase === "compose" && docPanelOpen && (
            <div className="cgpt-composer-wrap cgx cgx-dock">
              <button
                type="button"
                ref={attachBtnRef}
                className="cgx-attach is-open"
                aria-label="Attach files"
                aria-expanded="true"
                onClick={closeDocPanel}
              >
                <Ico name="paperclip" />
                <span>Attach files</span>
              </button>
              <div className="cgx-dock-chips">{attached.map(renderChip)}</div>
              <span className="cgx-dock-count">
                {decidedCount} of {docs.length} decided
              </span>
            </div>
          )}

          {phase === "reply" && replyStage < 3 && (
            <div className="cgpt-composer-wrap cgx">
              <div className="cgx-composer is-busy" aria-hidden>
                <span className="cgx-attach is-disabled">
                  <Ico name="paperclip" />
                  <span>Attach files</span>
                </span>
                <span className="cgx-ghost-input">Message {appName}&hellip;</span>
                <span className="cgx-send is-stop">
                  <Ico name="stop" />
                </span>
              </div>
            </div>
          )}

          {phase === "reply" && replyStage >= 3 && (
            <div className="cgx cgx-sharebar">
              <span className="cgx-sharebar-av" aria-hidden>
                {initialsOf(requesterName)}
              </span>
              <div className="cgx-sharebar-txt">
                <strong>{config.shareQuestion ?? `How do you get this summary to ${requesterFirst}?`}</strong>
                <span>
                  The summary now exists. This last step is about the safest way to get it to {requesterFirst}. Click Share
                  to decide.
                </span>
              </div>
              <button
                type="button"
                className="cgx-btn cgx-btn--primary cgx-btn--blink cgx-sharebar-btn"
                onClick={() => {
                  audio.click();
                  setShareOpen(true);
                }}
              >
                <Ico name="share" /> Share
              </button>
            </div>
          )}
        </section>
      </div>

      {/* share sheet (climax decision) */}
      {shareOpen && (
        <div className="cgpt-sharesheet-scrim cgx" onClick={() => !pendingLink && setShareOpen(false)}>
          <div
            className="cgpt-sharesheet cgx-sheet"
            role="dialog"
            aria-label="Share this summary"
            onClick={(e) => e.stopPropagation()}
          >
            {!pendingLink ? (
              <>
                <div className="cgpt-sharesheet-head">
                  <h3>Send the summary to {requesterName}</h3>
                  <button
                    type="button"
                    className="cgpt-sharesheet-x"
                    aria-label="Close"
                    onClick={() => {
                      audio.click();
                      setShareOpen(false);
                    }}
                  >
                    <Ico name="close" />
                  </button>
                </div>
                <p className="cgx-sheet-intro">
                  The summary now exists. This last decision is about the safest way to get it to {requesterFirst}. Think
                  about who else could end up able to read what you send.
                </p>
                <div className="cgpt-shareopts">
                  {(config.shareOptions ?? []).map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      className={`cgpt-shareopt cgpt-shareopt--${o.kind}`}
                      onClick={() => commitShare(o.kind)}
                    >
                      <span className="cgpt-shareopt-ico" aria-hidden>
                        <Ico name={SHARE_ICON[o.kind]} />
                      </span>
                      <span className="cgpt-shareopt-meta">
                        <span className="cgpt-shareopt-label">{o.label}</span>
                        {o.sublabel && <span className="cgpt-shareopt-sub">{o.sublabel}</span>}
                        <span className="cgx-leaves">
                          {SHARE_LEAVES[o.kind].map((l) => (
                            <span key={l.text} className={`cgx-leave cgx-leave--${l.tone}`}>
                              {l.text}
                            </span>
                          ))}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="cgpt-sharesheet-head">
                  <h3>Public link created</h3>
                </div>
                <div className="cgpt-linkbox">
                  <span className="cgpt-linkbox-ico" aria-hidden>
                    <Ico name="link" />
                  </span>
                  <span className="cgpt-linkbox-url">https://{chatUrl}/share/8f3c-northbay-summary</span>
                </div>
                <p className="cgpt-linknote">
                  {config.publicLinkNote ??
                    "Anyone with this link can view this conversation, including any files you attached."}
                </p>
                <div className="cgpt-sharesheet-foot">
                  <button type="button" className="cgpt-sharesheet-cancel" onClick={() => setPendingLink(false)}>
                    Back
                  </button>
                  <button type="button" className="cgpt-sharesheet-confirm" onClick={() => commitShare("public_link")}>
                    Copy link &amp; send to {requesterFirst}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* files window: one file at a time, then "Check your choices" */}
      {phase === "compose" && docPanelOpen && (
        <FileReviewPanel
          appName={appName}
          docs={docs}
          attached={attached}
          decisions={sendAsIs}
          redactions={redactions}
          currentId={currentId}
          shownId={shownId}
          pending={pending}
          view={panelView}
          workspace={workspace}
          prompt={prompt}
          onChoose={chooseDecision}
          onToggleLine={toggleRedact}
          onAttachRedacted={attachRedacted}
          onViewDoc={viewDoc}
          onReopen={reopenDoc}
          onClose={closeDocPanel}
          onCheck={goReview}
          onBackToFiles={() => {
            audio.click();
            setPanelView("files");
          }}
          onSend={startSend}
          onAsk={cancelAndAsk}
          onAccount={() => {
            audio.click();
            setProfileOpen((o) => !o);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** The end-of-episode teaching animation: a single public share link, then the
 *  many ways it can leave the company (indexing, malware, shared history,
 *  forwarding, no access control). Reachable on every path. */
function LinkEscape({
  config,
  onContinue,
}: {
  config: AiAssistantConfig;
  onContinue: () => void;
}) {
  const vectors = config.linkVectors ?? [];
  const [revealed, setRevealed] = useState(0);
  const spoke = useRef(false);

  useEffect(() => {
    if (spoke.current) return;
    spoke.current = true;
    if (config.linkExposureIntro) void audio.speak(config.linkExposureIntro, { voice: "onyx" });
    return () => audio.cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // stagger the vector cards in.
  useEffect(() => {
    if (revealed >= vectors.length) return;
    const t = setTimeout(() => {
      setRevealed((r) => r + 1);
      audio.click();
    }, 900);
    return () => clearTimeout(t);
  }, [revealed, vectors.length]);

  const done = revealed >= vectors.length;

  return (
    <div className="winapp cgpt-browser">
      <div className="cgpt-chrome">
        <span className="cgpt-dot cgpt-dot--r" aria-hidden />
        <span className="cgpt-dot cgpt-dot--y" aria-hidden />
        <span className="cgpt-dot cgpt-dot--g" aria-hidden />
        <div className="cgpt-omni" aria-hidden>
          <span className="cgpt-omni-url">Where a public link can travel</span>
        </div>
      </div>
      <div className="cgpt-escape">
        <div className="cgpt-escape-scroll">
          <p className="cgpt-escape-intro">
            {config.linkExposureIntro ??
              "A public link is not \u201Cjust a link to Priya.\u201D Once it exists, it can travel far past the person you sent it to:"}
          </p>

          <div className="cgpt-escape-hub" aria-hidden>
            <span className="cgpt-escape-link">
              {"\uD83D\uDD17"} https://chatgpt.com/share/8f3c…
            </span>
          </div>

          <div className="cgpt-escape-grid">
            {vectors.map((v, i) => (
              <div key={i} className={`cgpt-escape-card ${i < revealed ? "in" : ""}`}>
                <span className="cgpt-escape-ico" aria-hidden>
                  {v.icon}
                </span>
                <div className="cgpt-escape-card-meta">
                  <span className="cgpt-escape-card-title">{v.title}</span>
                  <span className="cgpt-escape-card-body">{v.body}</span>
                </div>
              </div>
            ))}
          </div>

          {done && config.linkExposureClosing && (
            <p className="cgpt-escape-closing">{config.linkExposureClosing}</p>
          )}
        </div>
        <button type="button" className="btn cgpt-result-continue" onClick={onContinue} disabled={!done}>
          {done ? "Continue" : "…"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function buildSummary(
  tier: AiChatResult["tier"],
  wsLabel: string,
  docs: string[],
  unnecessary: number,
  publicLink: boolean
): string {
  const docPart = docs.length
    ? `You uploaded ${docs.length === 1 ? "one document" : `${docs.length} documents`}`
    : "You sent a prompt with no attachments";
  const linkPart = publicLink ? " and turned the whole chat into a public link anyone can open" : "";
  if (tier === "critical") {
    return `${docPart} through ${wsLabel}${linkPart}. That exposed information the company cannot get back, including data that should never leave in a request like this.`;
  }
  if (tier === "high") {
    return `${docPart} through ${wsLabel}${linkPart}. Far more left the company than the summary actually required.`;
  }
  if (tier === "partial") {
    return `${docPart} through ${wsLabel}. The task can be done, but ${
      unnecessary > 0 ? "some information that wasn't needed came along with it" : "there is still room to tighten what you share"
    }.`;
  }
  return `You used ${wsLabel}, sent only what the summary needed, and got the result to Priya without a public link. Personal details, banking information and access codes stayed inside the company.`;
}

function buildRecommendation(config: AiAssistantConfig, tier: AiChatResult["tier"], publicLink: boolean): string {
  const corp = config.workspaces.find((w) => w.kind === "corporate")?.label ?? "the approved company workspace";
  if (tier === "best") {
    return "Keep doing this: the approved workspace, a trimmed excerpt, and an internal reply instead of a public link is exactly how to use AI on real customer material.";
  }
  if (tier === "safe_alt") {
    return "When a request touches confidential customer material and the rules aren't clear, pausing to ask is always a valid answer.";
  }
  if (publicLink) {
    return `Never turn a chat that contains company data into a public link. Paste the summary text into your reply instead, and use ${corp} with only the clauses the task needs.`;
  }
  return `Use ${corp} for anything touching real customer or contract data, attach only the specific clauses the task needs, and send the result through an internal channel \u2014 never a public link.`;
}
